import cn from 'clsx'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import {
  insertSelectedItems,
  removeSelectedItems,
} from '@disco/state-management/actions/ShortlistActions'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'
import {
  getShortlistsSelectedItems,
  makeGetSelectionIndexes,
} from '@disco/state-management/selectors/selection'
import {
  getVisibleShortlistCount,
  getVisibleShortlistIds,
  makeGetShortlistTracks,
} from '@disco/state-management/selectors/shortlists'
import { createNestedSetLocation } from '@disco/utils/nesting'
import { selectionContainNestedTracks } from '@disco/utils/selection'
import { areTrackTypesMixed } from '@disco/utils/tracks'

import type { Track } from 'types/track'

import { getShortlistBaseRoute } from 'utils/routing'

import ContextMenu from 'components/common/context-menu'
import ExplorerActionMenuContainer from 'components/explorer/action-menu/explorer-action-menu-container'
import { getShortlistEnabledClasses } from 'components/shortlist/actions/action-menu/menu-state'
import Button from 'components/ui/button'
import Tooltip from 'components/ui/tooltip'

function makeMapStateToProps() {
  const getSelectionIndexes = makeGetSelectionIndexes()
  const getShortlistTracks = makeGetShortlistTracks()
  return (state, props) => {
    const selectedItems = getShortlistsSelectedItems(state)
    return {
      shortlistIds: getVisibleShortlistIds(state),
      shortlistsCount: getVisibleShortlistCount(state),
      shortlistTracks: getShortlistTracks(state, props),
      selectedIndexes: getSelectionIndexes(state, props.shortlistId),
      selectedItems,
      selectedItemsWithinUserTerritories: selectedItems.filter(
        track => !track.outside_your_territories,
      ),
      location: getLocationBeforeTransitions(state),
    }
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      removeSelectedItems,
      insertSelectedItems,
    },
    dispatch,
  )
}

const itemClass = disabled =>
  cn('context-menu__option', {
    'is-disabled': disabled,
  })

type Props = {
  shortlistId: string
  selectedIndexes: Array<any>
  selectedItems: Array<any>
  shortlistIds: string[]
  shortlistsCount: number
  selectedItemsWithinUserTerritories: Track[]
  location: any
  removeSelectedItems: typeof removeSelectedItems
  insertSelectedItems: typeof insertSelectedItems
}

class ActionMenu extends Component<Props> {
  _handleItemsRemoval = closeMenu => {
    const { shortlistId, selectedIndexes } = this.props
    this.props.removeSelectedItems(shortlistId, selectedIndexes)
    closeMenu()
  }
  _handleAddToShortlist = closeMenu => {
    const { shortlistId, shortlistIds, selectedItems } = this.props
    const otherShorlistId = shortlistIds.find(id => id !== shortlistId)

    if (otherShorlistId) {
      this.props.insertSelectedItems(otherShorlistId, selectedItems)
    }

    closeMenu()
  }

  renderAddToShortlistAction(className, closeMenu) {
    if (this.props.shortlistsCount === 2) {
      return (
        <a
          className={className}
          onClick={() => this._handleAddToShortlist(closeMenu)}
        >
          Add to Playlist Creator
        </a>
      )
    }

    return (
      <Link
        className={className}
        to={`/selection/add-to-shortlist${
          this.props.shortlistId ? `?exclude=${this.props.shortlistId}` : ''
        }`}
        onClick={closeMenu}
      >
        Add to Playlist Creator
      </Link>
    )
  }

  _getEnabledClasses() {
    const {
      selectedItems,
      selectedItemsWithinUserTerritories,
      // TODO: clean-up props.shortlistTracks
      // shortlistTracks,
      shortlistsCount,
    } = this.props
    return getShortlistEnabledClasses({
      selectedItems,
      selectedItemsWithinUserTerritories,
      shortlistsCount,
    })
  }

  _getTooltipMessage(message?: string) {
    const { selectedItems } = this.props
    const nestedTracksHaveSelected = selectionContainNestedTracks(selectedItems)
    return nestedTracksHaveSelected
      ? 'This function is not supported for nested versions'
      : message
  }

  _renderActionList({ closeMenu }: { closeMenu?: () => void }) {
    const {
      shortlistId,
      selectedItems, // TODO: clean-up props.shortlistTracks
      // shortlistTracks,
    } = this.props

    const {
      actionEnabledClass,
      addToChannelClass,
      deleteActionEnabledClass,
      shortlistActionEnabledClass,
      manageNestingClass,
    } = this._getEnabledClasses()

    const nestedSetLocation = createNestedSetLocation(selectedItems)

    // TODO: clean-up
    // const dropdownClassName = cn('ShortlistActions_Menu_DropdownMenu-menu', {
    //   'is-open': true,
    // })
    const tooltipMessage = this._getTooltipMessage()

    return (
      <React.Fragment>
        <Tooltip content={tooltipMessage}>
          <a
            className={actionEnabledClass}
            onClick={() => this._handleItemsRemoval(closeMenu)}
          >
            Remove from shortlist
          </a>
        </Tooltip>
        <Tooltip content={tooltipMessage}>
          <Link
            className={addToChannelClass}
            to={`${getShortlistBaseRoute(
              this.props.location,
            )}/shortlists/${shortlistId}/add-to-channel`}
            onClick={closeMenu}
          >
            Add to channel
          </Link>
        </Tooltip>
        <Tooltip content={tooltipMessage}>
          {this.renderAddToShortlistAction(
            shortlistActionEnabledClass,
            closeMenu,
          )}
        </Tooltip>

        {nestedSetLocation && (
          <Link
            {...nestedSetLocation}
            className={manageNestingClass}
            onClick={closeMenu}
          >
            Manage nesting
          </Link>
        )}

        <Link
          className={cn(deleteActionEnabledClass, {
            'context-menu__option--warning':
              !deleteActionEnabledClass.includes('disabled'),
          })}
          to="/selection/delete?source=shortlist"
          onClick={closeMenu}
        >
          Delete from DISCO
        </Link>
      </React.Fragment>
    )
  }

  render() {
    const { shortlistId, selectedIndexes, selectedItems } = this.props

    if (selectedIndexes.length < 2) {
      return null
    }

    const { mergeEnabledClass, editSelectedClass, deleteActionEnabledClass } =
      this._getEnabledClasses()

    const mergeLink = areTrackTypesMixed(selectedItems)
      ? `/selection/merge-disabled`
      : `/selection/merge`
    return (
      <ExplorerActionMenuContainer
        selectedItems={selectedItems}
        buttons={[
          <Button
            key="edit-large"
            appearance="icon"
            compact
            icon="edit-large"
            tooltip={this._getTooltipMessage('Edit selected')}
            disabled={editSelectedClass.includes('disabled')}
            link={`${getShortlistBaseRoute(
              this.props.location,
            )}/shortlists/${shortlistId}/bulk-edit`}
          />,
          <Button
            key="merge"
            appearance="icon"
            compact
            icon="merge"
            tooltip={this._getTooltipMessage('Merge')}
            disabled={mergeEnabledClass.includes('disabled')}
            link={mergeLink}
          />,
          <Button
            key="delete-bucket"
            appearance="icon"
            compact
            icon="delete-bucket"
            tooltip="Delete selected"
            disabled={deleteActionEnabledClass.includes('disabled')}
            link={`/selection/delete?source=shortlist`}
          />,
        ]}
      >
        <ContextMenu
          menuClassName="ExplorerActionMenu_Modal"
          getMenuOffset={() => ['-194px', 'calc(-100% - 22px)']}
          appearance="action-menu"
          buttonProps={{
            split: undefined,
            appearance: 'icon',
            compact: true,
            icon: 'ellipsis',
            caption: '',
          }}
        >
          {props => this._renderActionList(props)}
        </ContextMenu>
      </ExplorerActionMenuContainer>
    )
  }
}

export default connect(
  makeMapStateToProps,
  mapDispatchToProps,
)(ActionMenu) as any
